import React from 'react';

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField?: string;
  onRowClick?: (item: T) => void;
  loading?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
}

export default function DataTable<T extends Record<string, any>>({
  columns,
  data,
  keyField = 'id',
  onRowClick,
  loading,
  emptyState,
  className = '',
}: DataTableProps<T>) {
  const shouldIgnoreRowClick = (
    event: React.MouseEvent<HTMLTableRowElement> | React.KeyboardEvent<HTMLTableRowElement>,
  ) => {
    const target = event.target as HTMLElement | null;
    const interactiveTarget = target?.closest('button, a, input, select, textarea, [role="button"]');
    return Boolean(interactiveTarget && interactiveTarget !== event.currentTarget);
  };

  if (loading) {
    return (
      <div className={`bg-surface-container-lowest rounded-card overflow-hidden ${className}`}>
        <div className="p-12 text-center text-on-surface-variant">
          <svg className="animate-spin h-6 w-6 mx-auto mb-2 text-on-surface-variant" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading...
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`bg-surface-container-lowest rounded-card overflow-hidden ${className}`}>
        <div className="p-12 text-center text-on-surface-variant">
          {emptyState || 'No data to display.'}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-surface-container-lowest rounded-card overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-surface-container-low">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider ${col.headerClassName || ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr
                key={item[keyField] ?? idx}
                className={`${idx % 2 === 1 ? 'bg-surface-container-low/50' : ''} ${onRowClick ? 'cursor-pointer hover:bg-surface-container-low transition-colors' : ''}`}
                onClick={
                  onRowClick
                    ? (event) => {
                        if (shouldIgnoreRowClick(event)) {
                          return;
                        }
                        onRowClick(item);
                      }
                    : undefined
                }
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (shouldIgnoreRowClick(event)) {
                          return;
                        }
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(item);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
              >
                {columns.map(col => (
                  <td key={col.key} className={`px-4 py-3 text-on-surface ${col.className || ''}`}>
                    {col.render ? col.render(item, idx) : item[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
